import { useMemo } from 'react';
import { Box, Paper } from '@mui/material';
import { ChatHeader } from './ChatHeader';
import { MessageThread } from './MessageThread';
import type { Conversation, Message, ChannelType } from '../../../types/chat.types';

interface TypingUser {
  userId: number;
  email?: string;
}

interface RealtimeReaction {
  reaction: string;
  userId?: number;
  contactIdentityId?: number;
}

interface ChatPanelProps {
  /** The conversation to display */
  conversation: Conversation;
  /** Messages in the conversation */
  messages: Message[];
  /** Current authenticated user ID */
  currentUserId: number;
  /** Array of users currently typing */
  typingUsers?: TypingUser[];
  /** Whether messages are loading */
  isLoading?: boolean;
  /** Whether there are more messages to load */
  hasMoreMessages?: boolean;
  /** Callback when user scrolls to load more messages */
  onLoadMore?: () => void;
  /** Whether more messages are being loaded */
  isLoadingMore?: boolean;
  /** Callback when details panel should be toggled */
  onToggleDetails?: () => void;
  /** Whether details panel is currently open */
  isDetailsOpen?: boolean;
  /** Callback to mute conversation notifications */
  onMuteConversation?: () => void;
  /** Callback to block the contact */
  onBlockContact?: () => void;
  /** Callback to edit the channel (channels only, creator only) */
  onEditChannel?: () => void;
  /** Callback to add participants (channels only, creator only) */
  onAddParticipants?: () => void;
  /** Callback to leave the conversation */
  onLeaveConversation?: () => void;
  /** Callback to delete the conversation (creator only) */
  onDeleteConversation?: () => void;
  /** Whether to show the details toggle button */
  showDetailsButton?: boolean;
  /** Message composer component (rendered at bottom) */
  messageComposer?: React.ReactNode;
  /** Minimum height of the panel */
  minHeight?: number | string;
  /** Maximum height of the panel */
  maxHeight?: number | string;
  /** Fixed height of the panel */
  height?: number | string;
  /** Show ticket quick actions in header */
  showTicketActions?: boolean;
  /** Real-time reactions from WebSocket, keyed by messageId */
  realtimeReactions?: Map<number, RealtimeReaction[]>;
  /** Callback when a message is updated (edit/delete/react) */
  onMessageUpdated?: () => void;
  /** Callback to retry sending a failed message */
  onRetryMessage?: (messageId: number) => void;
  /** Callback when user wants to reply/quote a message */
  onReply?: (message: Message) => void;
  /** Callback when user wants to forward a message */
  onForward?: (message: Message) => void;
}

/**
 * ChatPanel - Main container for a WhatsApp/Chatwoot-style chat UI
 *
 * Includes:
 * - ChatHeader: Contact info, online status, ticket badge, actions
 * - MessageThread: Virtual-scrolled message list with date separators
 * - MessageComposer slot: Placeholder for message input (provided externally)
 *
 * Features:
 * - Virtual scrolling for performance with large message lists
 * - Auto-scroll to bottom on new messages
 * - Infinite scroll (load more on scroll up)
 * - Typing indicators
 * - Date separators between message groups
 * - Responsive design
 *
 * @example
 * ```tsx
 * <ChatPanel
 *   conversation={conversation}
 *   messages={messages}
 *   currentUserId={userId}
 *   typingUsers={typingUsers}
 *   onLoadMore={handleLoadMore}
 *   hasMoreMessages={hasMore}
 *   onToggleDetails={() => setDetailsOpen(!detailsOpen)}
 *   isDetailsOpen={detailsOpen}
 *   messageComposer={<MessageComposer onSend={handleSend} />}
 * />
 * ```
 */
export function ChatPanel({
  conversation,
  messages,
  currentUserId,
  typingUsers = [],
  isLoading = false,
  hasMoreMessages = false,
  onLoadMore,
  isLoadingMore = false,
  onToggleDetails,
  isDetailsOpen = false,
  onMuteConversation,
  onBlockContact,
  onEditChannel,
  onAddParticipants,
  onLeaveConversation,
  onDeleteConversation,
  showDetailsButton = true,
  messageComposer,
  minHeight = 400,
  maxHeight,
  height,
  showTicketActions = true,
  realtimeReactions,
  onMessageUpdated,
  onRetryMessage,
  onReply,
  onForward,
}: ChatPanelProps) {
  // Resolve quotedMessage from local messages when only quotedMessageId is present
  // The API may not always include the full quotedMessage object
  const messagesWithResolvedQuotes = useMemo(() => {
    const hasUnresolved = messages.some((m) => m.quotedMessageId && !m.quotedMessage);
    if (!hasUnresolved) return messages;

    const messageMap = new Map(messages.map((m) => [m.id, m]));
    return messages.map((m) => {
      if (m.quotedMessageId && !m.quotedMessage) {
        const quoted = messageMap.get(m.quotedMessageId);
        if (quoted) {
          return { ...m, quotedMessage: quoted };
        }
      }
      return m;
    });
  }, [messages]);

  // Memoize conversation with tickets for header
  const conversationWithTickets = useMemo(() => {
    // If tickets are already on the conversation, use them
    if (conversation.tickets && conversation.tickets.length > 0) {
      return conversation;
    }

    // Extract unique tickets from messages if not on conversation
    const ticketsFromMessages = messages
      .filter((m) => m.ticket)
      .map((m) => m.ticket!)
      .filter((ticket, index, self) => self.findIndex((t) => t.uuid === ticket.uuid) === index);

    if (ticketsFromMessages.length > 0) {
      return {
        ...conversation,
        tickets: ticketsFromMessages,
      };
    }

    return conversation;
  }, [conversation, messages]);

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: height ?? 'calc(100vh - 200px)',
        minHeight,
        maxHeight,
        overflow: 'hidden',
        flex: height === '100%' ? 1 : undefined,
      }}
    >
      {/* Header */}
      <ChatHeader
        conversation={conversationWithTickets}
        onToggleDetails={onToggleDetails}
        isDetailsOpen={isDetailsOpen}
        showDetailsButton={showDetailsButton}
        onMuteConversation={onMuteConversation}
        onBlockContact={onBlockContact}
        onEditChannel={onEditChannel}
        onAddParticipants={onAddParticipants}
        onLeaveConversation={onLeaveConversation}
        onDeleteConversation={onDeleteConversation}
        currentUserId={currentUserId}
        showTicketActions={showTicketActions}
      />

      {/* Message thread */}
      <MessageThread
        messages={messagesWithResolvedQuotes}
        typingUsers={typingUsers}
        currentUserId={currentUserId}
        isLoading={isLoading}
        hasMoreMessages={hasMoreMessages}
        onLoadMore={onLoadMore}
        isLoadingMore={isLoadingMore}
        realtimeReactions={realtimeReactions}
        channelType={conversation.channelConnection?.channel?.type as ChannelType | undefined}
        onMessageUpdated={onMessageUpdated}
        onRetryMessage={onRetryMessage}
        onReply={onReply}
        onForward={onForward}
      />

      {/* Message composer placeholder */}
      {messageComposer && (
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          {messageComposer}
        </Box>
      )}
    </Paper>
  );
}
